import type { CSSProperties } from 'react'

import type { DynamicIslandActivity } from '../../../../context/dynamicIslandTypes'
import {
  activityIslandAccent,
  activityPreviewProgress,
  sortActivitiesByCategory,
} from '../../../../lib/dynamicIsland/categoryOrder'
import {
  dynamicIslandAccentClass,
  dynamicIslandProgressFillClass,
  dynamicIslandProgressTrackClass,
} from '../../../../lib/dynamicIsland/dynamicIslandPresentation'

type Props = {
  activities: DynamicIslandActivity[]
  selectedId: string
  onSelectId: (id: string) => void
  angular: boolean
  expanded: boolean
  flat?: boolean
  onExpand: () => void
}

function CategoryPreviewCard({
  activity,
  selected,
  angular,
  stacked,
  stackIndex,
  expanded,
  onClick,
}: {
  activity: DynamicIslandActivity
  selected: boolean
  angular: boolean
  stacked: boolean
  stackIndex: number
  expanded: boolean
  onClick: () => void
}) {
  const progress = activityPreviewProgress(activity)
  const accent = activityIslandAccent(activity)
  const showCompactChrome = !expanded
  const showCardProgress = showCompactChrome && (activity.lifecycle === 'active' || progress > 0)
  const progressTrack = dynamicIslandProgressTrackClass(angular)
  const progressFill = dynamicIslandProgressFillClass({
    accent,
    progressPercent: progress,
    lifecycle: activity.lifecycle,
  })

  return (
    <button
      type="button"
      className="glyphicai-dynamic-island-category-card"
      data-selected={selected ? 'true' : 'false'}
      data-stacked={stacked ? 'true' : 'false'}
      data-accent={accent}
      style={stacked ? { zIndex: stackIndex + 1 } : undefined}
      aria-label={activity.sourceLabel}
      aria-pressed={selected}
      onClick={(e) => {
        e.stopPropagation()
        onClick()
      }}
    >
      {showCompactChrome ? (
        <span
          className={['glyphicai-dynamic-island-activity__accent', dynamicIslandAccentClass(accent)].join(' ')}
          aria-hidden
          title={activity.sourceLabel}
        />
      ) : null}
      <span className="glyphicai-dynamic-island-category-card__body">
        <span className="glyphicai-dynamic-island-category-card__label">{activity.sourceLabel}</span>
        {showCardProgress ? (
          <span className={['glyphicai-dynamic-island-category-card__progress', progressTrack].join(' ')}>
            <span
              className={['glyphicai-dynamic-island-activity__progress-fill', progressFill].join(' ')}
              style={{ width: `${progress}%` }}
            />
          </span>
        ) : null}
      </span>
    </button>
  )
}

export function DynamicIslandCategoryPreviewRow({
  activities,
  selectedId,
  onSelectId,
  angular,
  expanded,
  flat = false,
  onExpand,
}: Props) {
  const ordered = sortActivitiesByCategory(activities)
  const stacked = !expanded && !flat

  const handleCardClick = (activityId: string) => {
    onSelectId(activityId)
    if (!expanded) onExpand()
  }

  return (
    <div
      className={[
        'glyphicai-dynamic-island-category-row',
        expanded ? 'glyphicai-dynamic-island-category-row--expanded' : '',
        flat ? 'glyphicai-dynamic-island-category-row--flat' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      style={{ '--island-category-count': ordered.length } as CSSProperties}
      role="list"
      aria-label="后台活动"
    >
      {ordered.map((activity, index) => (
        <CategoryPreviewCard
          key={activity.id}
          activity={activity}
          selected={activity.id === selectedId}
          angular={angular}
          stacked={stacked}
          stackIndex={index}
          expanded={expanded}
          onClick={() => handleCardClick(activity.id)}
        />
      ))}
    </div>
  )
}
